import { apiRequest } from './client.js';

/**
 * Employee-advances API (ADR-0009, PR ⑦a). HR-facing lifecycle under
 * /v1/advances + the employee self-service calls under /v1/me/advances.
 * Response types are declared locally (the backend route uses plain JSON;
 * no @kora/types schema yet).
 */

export interface AdvanceResponse {
  id: string;
  employeeId: string;
  kind: string;
  status: string;
  principalAmount: string;
  currency: string;
  monthlyDeduction: string;
  remainingBalance: string;
  startPeriod: string;
  disbursedAt: string | null;
  consentArtifactId: string | null;
  createdAt: string;
  // PR ⑦d — application lifecycle (nullable until the relevant step occurs).
  requestPurpose?: string | null;
  requestedTermMonths?: number | null;
  requestedAt?: string | null;
  approvedAt?: string | null;
  rejectedAt?: string | null;
  rejectedReason?: string | null;
}

export interface AdvanceLedgerLine {
  id?: string;
  kind: string;
  amount: string;
  effectiveDate: string;
  payrollRunId?: string | null;
  notes?: string | null;
}

export interface EmployeeRef {
  id: string;
  employeeNumber: string;
  firstName: string;
  lastName: string;
}

/** A list row now carries its employee (PR ⑦f), so the HR loans portfolio can
 *  show names without a second fetch. */
export interface AdvanceListRow extends AdvanceResponse {
  employee: EmployeeRef | null;
}

export interface AdvanceListResponse {
  advances: AdvanceListRow[];
}

export interface CreateAdvanceArgs {
  employeeId: string;
  kind: 'loan' | 'salary_advance';
  principalAmount: string;
  currency: string;
  monthlyDeduction: string;
  startPeriod: string;
  consentArtifactId?: string;
  notes?: string;
}

/** Loan-migration: capture a pre-existing loan (lands directly in `active`). */
export interface MigrateAdvanceArgs {
  employeeId: string;
  kind: 'loan' | 'salary_advance';
  principalAmount: string;
  currency: string;
  monthlyDeduction: string;
  startPeriod: string;
  remainingBalance: string;
  originalDisbursementDate: string;
  sourceSystem: string;
  consentArtifactId: string;
  concurrencyOverrideReason?: string;
  notes?: string;
}

export function listAdvances(args: { employeeId?: string; status?: string } = {}): Promise<AdvanceListResponse> {
  const params = new URLSearchParams();
  if (args.employeeId) params.set('employeeId', args.employeeId);
  if (args.status) params.set('status', args.status);
  const qs = params.toString();
  return apiRequest<AdvanceListResponse>(`/v1/advances${qs ? `?${qs}` : ''}`);
}

export function getAdvance(id: string): Promise<AdvanceResponse & { ledger: AdvanceLedgerLine[] }> {
  return apiRequest(`/v1/advances/${encodeURIComponent(id)}`);
}

export function migrateAdvance(body: MigrateAdvanceArgs): Promise<AdvanceResponse> {
  return apiRequest<AdvanceResponse>('/v1/advances/migrate', { method: 'POST', body });
}

export function createAdvance(body: CreateAdvanceArgs): Promise<AdvanceResponse> {
  return apiRequest<AdvanceResponse>('/v1/advances', { method: 'POST', body });
}

export function confirmDisbursement(id: string): Promise<AdvanceResponse> {
  return apiRequest<AdvanceResponse>(`/v1/advances/${encodeURIComponent(id)}/confirm-disbursement`, { method: 'POST' });
}

export function forgiveAdvance(id: string, reason?: string): Promise<AdvanceResponse> {
  return apiRequest<AdvanceResponse>(`/v1/advances/${encodeURIComponent(id)}/forgive`, { method: 'POST', body: { reason } });
}

export function cancelAdvance(id: string): Promise<AdvanceResponse> {
  return apiRequest<AdvanceResponse>(`/v1/advances/${encodeURIComponent(id)}/cancel`, { method: 'POST' });
}

// --- HR review of employee applications (PR ⑦d) ---------------------------

export interface AdvanceRequest extends AdvanceResponse {
  employee: { id: string; employeeNumber: string; firstName: string; lastName: string } | null;
}

export function listAdvanceRequests(status = 'requested'): Promise<{ requests: AdvanceRequest[] }> {
  return apiRequest(`/v1/advances/requests?status=${encodeURIComponent(status)}`);
}

export interface ApproveAdvanceArgs {
  adjustedPrincipal?: string;
  adjustedMonthlyDeduction?: string;
  adjustedTermMonths?: number;
  consentArtifactId?: string;
  notes?: string;
}

export function approveAdvance(id: string, body: ApproveAdvanceArgs = {}): Promise<AdvanceResponse> {
  return apiRequest<AdvanceResponse>(`/v1/advances/${encodeURIComponent(id)}/approve`, { method: 'POST', body });
}

export function rejectAdvance(id: string, reason: string): Promise<AdvanceResponse> {
  return apiRequest<AdvanceResponse>(`/v1/advances/${encodeURIComponent(id)}/reject`, { method: 'POST', body: { reason } });
}

// --- employee self-service (/me) ------------------------------------------

export interface MyAdvance {
  id: string;
  kind: string;
  status: string;
  principalAmount: string;
  currency: string;
  monthlyDeduction: string;
  remainingBalance: string;
  startPeriod: string;
  disbursedAt: string | null;
  // PR ⑦d — application context shown to the employee.
  requestPurpose?: string | null;
  requestedTermMonths?: number | null;
  requestedAt?: string | null;
  approvedAt?: string | null;
  rejectedAt?: string | null;
  rejectedReason?: string | null;
  ledger: AdvanceLedgerLine[];
}

export function listMyAdvances(): Promise<{ advances: MyAdvance[] }> {
  return apiRequest<{ advances: MyAdvance[] }>('/v1/me/advances');
}

export function confirmMyAdvanceReceipt(id: string): Promise<{ id: string; status: string }> {
  return apiRequest(`/v1/me/advances/${encodeURIComponent(id)}/confirm-receipt`, { method: 'POST' });
}

// --- employee loan applications (PR ⑦d) -----------------------------------

export interface RequestLoanArgs {
  kind: 'loan' | 'salary_advance';
  principalAmount: string;
  currency: string;
  requestedTermMonths: number;
  requestPurpose: string;
  monthlyDeduction?: string;
  startPeriod?: string;
}

export function requestLoan(body: RequestLoanArgs): Promise<MyAdvance> {
  return apiRequest<MyAdvance>('/v1/me/advances/request', { method: 'POST', body });
}

export function cancelMyRequest(id: string): Promise<{ id: string; status: string }> {
  return apiRequest(`/v1/me/advances/${encodeURIComponent(id)}/cancel-request`, { method: 'POST' });
}
